import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import {
    Activity, Zap, Radio, Eye, Brain, RefreshCw, Clock,
    AlertTriangle, CheckCircle, TrendingUp, Shield, Loader2,
    Target, BarChart3, Wifi, WifiOff, Users, ArrowRight
} from 'lucide-react';

const STATUS_COLORS = {
    idle: { bg: 'bg-white/5', text: 'text-white/30', dot: 'bg-white/20' },
    active: { bg: 'bg-green-500/10', text: 'text-green-400', dot: 'bg-green-400' },
    scanning: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', dot: 'bg-cyan-400' },
    analyzing: { bg: 'bg-purple-500/10', text: 'text-purple-400', dot: 'bg-purple-400' },
    waiting: { bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-400' },
    monitoring: { bg: 'bg-blue-500/10', text: 'text-blue-400', dot: 'bg-blue-400' },
    error: { bg: 'bg-red-500/10', text: 'text-red-400', dot: 'bg-red-400' },
    post_toss: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-400' },
};

function StatusDot({ status }) {
    const c = STATUS_COLORS[status] || STATUS_COLORS.idle;
    return (
        <span className="relative flex h-2.5 w-2.5">
            {['active', 'scanning', 'analyzing'].includes(status) && (
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-50 ${c.dot}`}></span>
            )}
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${c.dot}`}></span>
        </span>
    );
}

export default function RealtimeCommand() {
    const [realtimeStatus, setRealtimeStatus] = useState(null);
    const [scheduler, setScheduler] = useState(null);
    const [overAccuracy, setOverAccuracy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [triggering, setTriggering] = useState(false);
    const [triggerResult, setTriggerResult] = useState(null);
    const [retrainingLog, setRetrainingLog] = useState(null);
    const [fixtures, setFixtures] = useState([]);

    const refresh = useCallback(async () => {
        try {
            const [rtRes, schedRes, accRes, fxRes, logRes] = await Promise.all([
                API.get('/live/realtime-status'),
                API.get('/intelligence/scheduler'),
                API.get('/live/over-accuracy'),
                API.get('/fixtures?season=IPL%202026'),
                API.get('/season-preview/retraining-log'),
            ]);
            setRealtimeStatus(rtRes.data);
            setScheduler(schedRes.data);
            setOverAccuracy(accRes.data);
            setFixtures((fxRes.data.fixtures || []).filter(f => f.status !== 'completed').slice(0, 5));
            setRetrainingLog(logRes.data);
        } catch (err) { console.error(err); }
        setLoading(false);
    }, []);

    useEffect(() => { refresh(); const iv = setInterval(refresh, 15000); return () => clearInterval(iv); }, [refresh]);

    const handleTrigger = async (matchId) => {
        setTriggering(true);
        setTriggerResult(null);
        try {
            const res = await API.post(`/intelligence/trigger/${matchId}`);
            setTriggerResult(res.data);
            refresh();
        } catch (err) { console.error(err); }
        setTriggering(false);
    };

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="glass-card p-12 text-center">
                    <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
            </div>
        );
    }

    const sysStatus = realtimeStatus?.system_status || 'monitoring';
    const sc = STATUS_COLORS[sysStatus] || STATUS_COLORS.monitoring;
    const schedStatus = scheduler?.status || 'idle';
    const nextMatch = scheduler?.next_match;
    const liveCnt = realtimeStatus?.live_matches || 0;

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5" data-testid="realtime-command">
            {/* Header */}
            <div className={`glass-card p-5 border-l-4 ${sysStatus === 'active' || liveCnt > 0 ? 'border-l-green-500' : 'border-l-cyan-500'}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Radio className="w-5 h-5 text-cyan-400" />
                        <div>
                            <h1 className="heading-primary text-base md:text-xl text-white">REAL-TIME COMMAND CENTER</h1>
                            <div className="text-xs text-white/30 mt-0.5">
                                Impact Player Detection | Ball-by-Ball | Auto-Intelligence
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <StatusDot status={sysStatus} />
                        <span className={`text-[10px] px-2 py-1 rounded tech-mono ${sc.bg} ${sc.text}`}>
                            {sysStatus.toUpperCase()}
                        </span>
                        <Button variant="ghost" size="sm" onClick={refresh} className="text-white/30 hover:text-white">
                            <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Status Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="glass-card p-4 border border-white/5" data-testid="live-matches-card">
                    <div className="flex items-center gap-2 mb-2">
                        {liveCnt > 0 ? <Wifi className="w-4 h-4 text-green-400" /> : <WifiOff className="w-4 h-4 text-white/15" />}
                        <span className="text-[10px] text-white/30 tech-mono">LIVE MATCHES</span>
                    </div>
                    <div className={`text-2xl font-bold ${liveCnt > 0 ? 'text-green-400' : 'text-white/15'}`}>{liveCnt}</div>
                    <div className="text-[10px] text-white/20">{liveCnt > 0 ? 'Monitoring in real-time' : 'No live matches'}</div>
                </div>

                <div className="glass-card p-4 border border-white/5" data-testid="scheduler-card">
                    <div className="flex items-center gap-2 mb-2">
                        <StatusDot status={schedStatus} />
                        <span className="text-[10px] text-white/30 tech-mono">AUTO-INTELLIGENCE</span>
                    </div>
                    <div className={`text-sm font-bold ${STATUS_COLORS[schedStatus]?.text || 'text-white/30'}`}>
                        {schedStatus.toUpperCase().replace('_', ' ')}
                    </div>
                    <div className="text-[10px] text-white/20">
                        {scheduler?.cycle_count || 0} cycles completed
                    </div>
                </div>

                <div className="glass-card p-4 border border-white/5" data-testid="impact-card">
                    <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-amber-400/50" />
                        <span className="text-[10px] text-white/30 tech-mono">IMPACT PLAYERS</span>
                    </div>
                    <div className="text-sm font-bold text-white/30">
                        {realtimeStatus?.matches?.reduce((a, m) => a + (m.impact_count || 0), 0) || 0} Detected
                    </div>
                    <div className="text-[10px] text-white/20">Substitution monitoring</div>
                </div>

                <div className="glass-card p-4 border border-white/5" data-testid="bbb-card">
                    <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="w-4 h-4 text-blue-400/50" />
                        <span className="text-[10px] text-white/30 tech-mono">BALL-BY-BALL</span>
                    </div>
                    <div className="text-sm font-bold text-white/30">
                        {overAccuracy?.total_overs_tracked || 0} Overs
                    </div>
                    <div className="text-[10px] text-white/20">
                        {overAccuracy?.total_overs_tracked > 0 ? `Avg deviation: ${overAccuracy.avg_deviation}` : 'Tracking begins at match start'}
                    </div>
                </div>
            </div>

            {/* Next Match / Auto-Intelligence */}
            <div className="glass-card p-5 border border-white/5" data-testid="next-match-section">
                <div className="flex items-center gap-2 mb-4">
                    <Brain className="w-4 h-4 text-purple-400" />
                    <h2 className="heading-primary text-sm text-white">AUTO-INTELLIGENCE SCHEDULER</h2>
                </div>

                {nextMatch ? (
                    <div className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-lg p-4 border border-purple-500/20 mb-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm font-bold text-white">{nextMatch.teams}</div>
                                <div className="text-xs text-white/30">{new Date(nextMatch.date).toLocaleString()}</div>
                                <div className="text-[10px] text-white/20 tech-mono mt-1">
                                    {nextMatch.minutes_until > 60
                                        ? `${Math.round(nextMatch.minutes_until / 60)}h ${Math.round(nextMatch.minutes_until % 60)}m until match`
                                        : nextMatch.minutes_until > 0
                                            ? `${Math.round(nextMatch.minutes_until)}m until match — Intelligence gathering ${nextMatch.minutes_until <= 60 ? 'ACTIVE' : 'will start in ' + Math.round(nextMatch.minutes_until - 60) + 'm'}`
                                            : 'Match starting now'
                                    }
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {nextMatch.minutes_until <= 60 && nextMatch.minutes_until > 0 && (
                                    <span className="text-[10px] px-2 py-1 rounded bg-green-500/20 text-green-400 tech-mono animate-pulse">
                                        SCANNING
                                    </span>
                                )}
                                <StatusDot status={schedStatus} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-xs text-white/20 mb-4">
                        No upcoming matches detected. Scheduler will activate 1 hour before next match.
                    </div>
                )}

                {/* Intelligence Cycle Trigger */}
                <div className="flex items-center justify-between">
                    <div className="text-xs text-white/30">
                        {scheduler?.last_run
                            ? `Last scan: ${new Date(scheduler.last_run).toLocaleString()}`
                            : 'No scans completed yet'}
                    </div>
                </div>

                {/* Recent Cycles */}
                {scheduler?.recent_cycles?.length > 0 && (
                    <div className="mt-4 space-y-2">
                        <div className="text-[10px] text-white/20 tech-mono">RECENT CYCLES</div>
                        {scheduler.recent_cycles.map((c, i) => (
                            <div key={i} className="flex items-center justify-between bg-white/3 rounded-lg p-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-white/20 tech-mono">#{c.cycle}</span>
                                    <span className="text-xs text-white/50">{c.sources_scanned} sources, {c.total_insights} insights</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {c.has_adjustment && <CheckCircle className="w-3 h-3 text-green-400" />}
                                    <span className="text-[10px] text-white/15">{new Date(c.timestamp).toLocaleTimeString()}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Manual Intelligence Trigger */}
            <div className="glass-card p-5 border border-white/5" data-testid="manual-trigger">
                <div className="flex items-center gap-2 mb-4">
                    <Target className="w-4 h-4 text-amber-400" />
                    <h2 className="heading-primary text-sm text-white">MANUAL INTELLIGENCE TRIGGER</h2>
                    <span className="text-[10px] text-white/20">Select a match to run intelligence scan</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {fixtures.map(f => (
                        <button key={f.id} onClick={() => handleTrigger(f.id)} disabled={triggering}
                            className="bg-white/3 hover:bg-white/5 border border-white/5 hover:border-cyan-500/20 rounded-lg p-3 text-left transition-all"
                            data-testid={`trigger-${f.id}`}>
                            <div className="text-sm font-bold text-white">
                                {f.home_team?.short_name} vs {f.away_team?.short_name}
                            </div>
                            <div className="text-[10px] text-white/30 tech-mono">{f.date?.slice(0, 10)}</div>
                            <div className="text-[10px] text-white/15">{f.venue?.city}</div>
                        </button>
                    ))}
                </div>
                {triggering && (
                    <div className="flex items-center gap-2 mt-3 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                        <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                        <span className="text-xs text-cyan-400">Scanning social media and generating intelligence report...</span>
                    </div>
                )}
            </div>

            {/* Trigger Result */}
            {triggerResult && (
                <div className="glass-card p-5 border border-emerald-500/20" data-testid="trigger-result">
                    <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        <h3 className="heading-primary text-sm text-white">INTELLIGENCE REPORT</h3>
                        <span className="text-[10px] text-white/20 tech-mono">Cycle #{triggerResult.cycle}</span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="bg-white/3 rounded-lg p-3 text-center">
                            <div className="text-[10px] text-white/30 tech-mono">SOURCES</div>
                            <div className="text-lg font-bold text-cyan-400">{triggerResult.sources_scanned}</div>
                        </div>
                        <div className="bg-white/3 rounded-lg p-3 text-center">
                            <div className="text-[10px] text-white/30 tech-mono">INSIGHTS</div>
                            <div className="text-lg font-bold text-purple-400">{triggerResult.total_insights}</div>
                        </div>
                        <div className="bg-white/3 rounded-lg p-3 text-center">
                            <div className="text-[10px] text-white/30 tech-mono">CONFIDENCE</div>
                            <div className={`text-sm font-bold ${triggerResult.report?.confidence_level === 'HIGH' ? 'text-green-400' : triggerResult.report?.confidence_level === 'MEDIUM' ? 'text-amber-400' : 'text-white/30'}`}>
                                {triggerResult.report?.confidence_level || 'N/A'}
                            </div>
                        </div>
                        <div className="bg-white/3 rounded-lg p-3 text-center">
                            <div className="text-[10px] text-white/30 tech-mono">ADJUSTED</div>
                            <div className={`text-sm font-bold ${triggerResult.prediction_adjustment?.adjusted ? 'text-green-400' : 'text-white/30'}`}>
                                {triggerResult.prediction_adjustment?.adjusted ? 'YES' : 'NO'}
                            </div>
                        </div>
                    </div>

                    {/* Report Details */}
                    {triggerResult.report?.generated && (
                        <div className="space-y-2">
                            {triggerResult.report.sentiment_score && (
                                <div className="bg-white/3 rounded-lg p-3">
                                    <span className="text-[10px] text-white/30 tech-mono">SENTIMENT: </span>
                                    <span className="text-xs text-white/60">{triggerResult.report.sentiment_score}</span>
                                </div>
                            )}
                            {triggerResult.report.expert_consensus && (
                                <div className="bg-white/3 rounded-lg p-3">
                                    <span className="text-[10px] text-white/30 tech-mono">EXPERT CONSENSUS: </span>
                                    <span className="text-xs text-white/60">{triggerResult.report.expert_consensus}</span>
                                </div>
                            )}
                            {triggerResult.report.risk_factors && (
                                <div className="bg-white/3 rounded-lg p-3">
                                    <span className="text-[10px] text-white/30 tech-mono">RISK FACTORS: </span>
                                    <span className="text-xs text-white/60">{triggerResult.report.risk_factors}</span>
                                </div>
                            )}
                            {triggerResult.report.impact_player_expectations && (
                                <div className="bg-white/3 rounded-lg p-3">
                                    <span className="text-[10px] text-white/30 tech-mono">IMPACT PLAYER: </span>
                                    <span className="text-xs text-white/60">{triggerResult.report.impact_player_expectations}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Prediction Adjustment */}
                    {triggerResult.prediction_adjustment?.adjusted && (
                        <div className="mt-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg p-4 border border-green-500/20">
                            <div className="text-[10px] text-white/30 tech-mono mb-2">PREDICTION ADJUSTMENT</div>
                            <div className="grid grid-cols-3 gap-3 text-center">
                                <div>
                                    <div className="text-[10px] text-white/20">ORIGINAL</div>
                                    <div className="text-sm font-bold text-white/50">
                                        {triggerResult.prediction_adjustment.original?.team_a}% - {triggerResult.prediction_adjustment.original?.team_b}%
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] text-white/20">ADJUSTMENT</div>
                                    <div className={`text-sm font-bold ${triggerResult.prediction_adjustment.adjustment?.net > 0 ? 'text-green-400' : triggerResult.prediction_adjustment.adjustment?.net < 0 ? 'text-red-400' : 'text-white/30'}`}>
                                        {triggerResult.prediction_adjustment.adjustment?.net > 0 ? '+' : ''}{triggerResult.prediction_adjustment.adjustment?.net}%
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] text-white/20">FINAL</div>
                                    <div className="text-sm font-bold text-emerald-400">
                                        {triggerResult.prediction_adjustment.final?.team_a}% - {triggerResult.prediction_adjustment.final?.team_b}%
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Live Match Details */}
            {realtimeStatus?.matches?.length > 0 && (
                <div className="glass-card p-5 border border-green-500/20" data-testid="live-matches-detail">
                    <div className="flex items-center gap-2 mb-3">
                        <Activity className="w-4 h-4 text-green-400 animate-pulse" />
                        <h2 className="heading-primary text-sm text-white">LIVE MATCH MONITORING</h2>
                    </div>
                    <div className="space-y-3">
                        {realtimeStatus.matches.map(m => (
                            <div key={m.match_id} className="bg-green-500/5 border border-green-500/10 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="text-sm font-bold text-white">{m.teams}</div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-[10px] text-white/20 tech-mono">
                                            Impact: {m.impact_count} | Overs: {m.overs_tracked} | Adj: {m.total_adjustment > 0 ? '+' : ''}{m.total_adjustment}%
                                        </div>
                                    </div>
                                </div>
                                {/* Impact player substitution details */}
                                {m.impact_count > 0 && (
                                    <div className="mt-2 space-y-1">
                                        <div className="text-[10px] text-amber-400/60 tech-mono">IMPACT SUBSTITUTIONS DETECTED</div>
                                        <div className="flex items-center gap-2 bg-amber-500/10 rounded-lg p-2">
                                            <Zap className="w-3.5 h-3.5 text-amber-400" />
                                            <span className="text-xs text-amber-300">{m.impact_count} substitution(s) — Win% recalculated</span>
                                        </div>
                                    </div>
                                )}
                                {/* Ball-by-ball tracking status */}
                                {m.overs_tracked > 0 && (
                                    <div className="mt-2 flex items-center gap-2 bg-blue-500/10 rounded-lg p-2">
                                        <BarChart3 className="w-3.5 h-3.5 text-blue-400" />
                                        <span className="text-xs text-blue-300">
                                            {m.overs_tracked} overs tracked | Net adjustment: {m.total_adjustment > 0 ? '+' : ''}{m.total_adjustment}%
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Auto-Retrain Log */}
            <div className="glass-card p-5 border border-white/5" data-testid="retrain-log">
                <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-4 h-4 text-purple-400" />
                    <h2 className="heading-primary text-sm text-white">AUTO-RETRAIN LOG</h2>
                    <span className="text-[10px] text-white/20 tech-mono">
                        {retrainingLog?.total_retrained || 0} post-match retrains
                    </span>
                </div>
                {retrainingLog?.retraining_events?.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                        {retrainingLog.retraining_events.slice(0, 5).map((e, i) => (
                            <div key={i} className="flex items-center justify-between bg-white/3 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                                    <div>
                                        <div className="text-xs text-white/60">Retrained after match completion</div>
                                        <div className="text-[10px] text-white/20">{e.correct}/{e.total_predictions} predictions correct</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-cyan-400">{e.accuracy_after}%</div>
                                    <div className="text-[10px] text-white/15">{e.timestamp ? new Date(e.timestamp).toLocaleDateString() : ''}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <div className="text-xs text-white/20">No auto-retraining events yet</div>
                        <div className="text-[10px] text-white/10 mt-1">Model will auto-retrain when live matches complete</div>
                    </div>
                )}
            </div>

            {/* System Rules */}
            <div className="glass-card p-4 border border-white/5">
                <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-3.5 h-3.5 text-white/20" />
                    <span className="text-[10px] text-white/15 tech-mono">SYSTEM BEHAVIOR</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px] text-white/15">
                    <div className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-green-400/30" />
                        Auto-triggers 1h before match
                    </div>
                    <div className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-green-400/30" />
                        Scans every 5 minutes pre-match
                    </div>
                    <div className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-green-400/30" />
                        Merges with toss data at match start
                    </div>
                    <div className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-green-400/30" />
                        Ball-by-ball tracking during live match
                    </div>
                    <div className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-green-400/30" />
                        Impact player substitution detection
                    </div>
                    <div className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-green-400/30" />
                        All insights stored for model training
                    </div>
                </div>
            </div>
        </div>
    );
}
